/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import {
  getCostControlYear, updateCostControlMonth, syncCostControlFromSheets,
  getCostControlSheetPreview,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Loader2, RefreshCw, Wallet, TrendingDown, TrendingUp, Sheet, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const ES_MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
                   "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

const eur = (n) =>
  (Number(n) || 0).toLocaleString("es-ES", {
    style: "currency", currency: "EUR", minimumFractionDigits: 2,
  });

// Editable currency cell that commits on blur
function EditableCell({ value, onCommit, disabled }) {
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);
  return (
    <input
      type="number"
      step="0.01"
      value={local === 0 ? "" : local}
      placeholder="0,00"
      disabled={disabled}
      onChange={(e) => setLocal(e.target.value === "" ? 0 : parseFloat(e.target.value))}
      onBlur={() => {
        const n = Number(local) || 0;
        if (n !== value) onCommit(n);
      }}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      className="w-24 px-2 py-1 text-right text-xs font-mono bg-white border border-slate-200 rounded hover:border-slate-400 focus:border-slate-900 focus:outline-none disabled:bg-slate-50 disabled:text-slate-400"
    />
  );
}

export default function CostControl() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [sheetPreview, setSheetPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getCostControlYear(year);
      setData(d);
    } catch {
      toast.error("Error cargando control de costes");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const changeYear = (y) => {
    if (y === year) return;
    // Reset the cached sheet preview when switching years
    setSheetPreview(null);
    setShowPreview(false);
    setYear(y);
  };

  const handleEdit = async (month, field, value) => {
    try {
      const d = await updateCostControlMonth(year, month, { [field]: value });
      setData(d);
      toast.success(`${ES_MONTHS[month-1]} actualizado`);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await syncCostControlFromSheets(year);
      setData(r.data);
      const cur = r.data?.totals?.deuda_et_current ?? r.data?.totals?.deuda_et_final ?? 0;
      const monthIdx = r.data?.totals?.current_month || 0;
      const monthLabel = monthIdx > 0 ? ES_MONTHS[monthIdx - 1] : "—";
      const sign = cur >= 0 ? "ET debe" : "VDV debe";
      toast.success(
        `Sincronizados ${r.updated.length} meses · Saldo a fin de ${monthLabel}: ${sign} ${eur(Math.abs(cur))}`
      );
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  const togglePreview = async () => {
    if (showPreview) {
      setShowPreview(false);
      return;
    }
    if (!sheetPreview) {
      setPreviewLoading(true);
      try {
        const p = await getCostControlSheetPreview(year);
        setSheetPreview(p);
      } catch (e) {
        toast.error("Error: " + (e?.response?.data?.detail || e.message));
        setPreviewLoading(false);
        return;
      }
      setPreviewLoading(false);
    }
    setShowPreview(true);
  };

  if (loading || !data) {
    return <div className="text-slate-400 text-sm py-12 text-center">Cargando…</div>;
  }

  const ES_MONTHS_FULL = ES_MONTHS;
  const finalDeuda = data.totals.deuda_et_final;
  const currentDeuda = data.totals.deuda_et_current ?? finalDeuda;
  const currentMonth = data.totals.current_month || 0;
  const deudaIsOwed = currentDeuda > 0;
  const years = [];
  for (let y = 2025; y <= new Date().getFullYear() + 1; y++) years.push(y);

  const currentMonthLabel = currentMonth > 0
    ? `${ES_MONTHS_FULL[currentMonth - 1]} ${year}`
    : `1-Ene-${year} (sin movimientos)`;

  return (
    <div data-testid="cost-control-page" className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-1">
            Control de costes · VdV ↔ ET
          </div>
          <h1 className="font-cabinet text-3xl font-bold tracking-tight text-slate-900">
            Balance de servicios compartidos
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Gastos compartidos al 50%. PAGADO ET se calcula automáticamente de las
            facturas de adelanto y finales emitidas cada mes (líneas de servicios).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {years.map((y) => (
            <button
              key={y}
              onClick={() => changeYear(y)}
              data-testid={`cost-year-${y}`}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                y === year
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {y}
            </button>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="ml-2"
            onClick={handleSync}
            disabled={syncing}
            data-testid="sync-sheets-btn"
          >
            {syncing
              ? <Loader2 size={13} className="mr-1.5 animate-spin" />
              : <RefreshCw size={13} className="mr-1.5" />}
            Sincronizar desde Sheets
          </Button>
        </div>
      </header>

      {/* KPI bar */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className={`rounded-lg border-2 p-4 ${
          deudaIsOwed ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"
        }`}>
          <div className="flex items-start justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">
              {deudaIsOwed ? "ET debe a VDV" : "VDV debe a ET"}
            </div>
            {deudaIsOwed
              ? <TrendingUp size={16} className="text-rose-600" />
              : <TrendingDown size={16} className="text-emerald-600" />}
          </div>
          <div className={`font-cabinet text-3xl font-bold ${
            deudaIsOwed ? "text-rose-700" : "text-emerald-700"
          }`} data-testid="cost-deuda-current">
            {eur(Math.abs(currentDeuda))}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            Saldo actualizado a fin de <strong>{currentMonthLabel}</strong>
          </div>
          {Math.abs(finalDeuda - currentDeuda) > 0.01 && (
            <div className="text-[10px] text-slate-400 mt-0.5">
              Proyección 31-Dic-{year}: {eur(Math.abs(finalDeuda))}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">
              Gastos VDV {year}
            </div>
            <Wallet size={16} className="text-slate-400" />
          </div>
          <div className="font-cabinet text-2xl font-bold text-slate-900">
            {eur(data.totals.gastos_vdv)}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            A pagar ET (50%): <strong>{eur(data.totals.a_pagar_et)}</strong>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-2">
            Facturado a ET {year}
          </div>
          <div className="font-cabinet text-2xl font-bold text-slate-900">
            {eur(data.totals.pagado_et)}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            Suma líneas de servicios en facturas emitidas
          </div>
        </div>
      </div>

      <div className="text-[11px] text-slate-500 -mt-2">
        Saldo a 1-Ene-{year}: <strong>{eur(data.starting_deuda)}</strong>
      </div>

      {/* Matrix */}
      <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <th className="text-left px-3 py-2.5 font-medium sticky left-0 bg-slate-50 z-10">Concepto</th>
              {ES_MONTHS.map((m) => (
                <th key={m} className="text-right px-2 py-2.5 font-medium">{m.slice(0, 3)}</th>
              ))}
              <th className="text-right px-3 py-2.5 font-medium border-l border-slate-200 bg-slate-100">Total</th>
            </tr>
          </thead>
          <tbody className="text-xs">
            {/* Editable row: Gastos VDV */}
            <tr className="border-t border-slate-100">
              <td className="px-3 py-2 font-medium sticky left-0 bg-white z-10 border-r border-slate-100">
                Gastos VDV
                <div className="text-[9px] text-slate-400">desde Sheets o manual</div>
              </td>
              {data.rows.map((r) => (
                <td key={r.month} className="px-1 py-1 text-right">
                  <EditableCell
                    value={r.gastos_vdv}
                    onCommit={(v) => handleEdit(r.month, "gastos_vdv", v)}
                  />
                </td>
              ))}
              <td className="px-3 py-2 text-right font-mono font-semibold bg-slate-50">
                {eur(data.totals.gastos_vdv)}
              </td>
            </tr>

            {/* Editable row: Otros Pagos VDV */}
            <tr className="border-t border-slate-100">
              <td className="px-3 py-2 font-medium sticky left-0 bg-white z-10 border-r border-slate-100">
                Otros Pagos VDV
                <div className="text-[9px] text-slate-400">descuento manual</div>
              </td>
              {data.rows.map((r) => (
                <td key={r.month} className="px-1 py-1 text-right">
                  <EditableCell
                    value={r.otros_pagos_vdv}
                    onCommit={(v) => handleEdit(r.month, "otros_pagos_vdv", v)}
                  />
                </td>
              ))}
              <td className="px-3 py-2 text-right font-mono font-semibold bg-slate-50">
                {eur(data.totals.otros_pagos_vdv)}
              </td>
            </tr>

            {/* Editable row: Ayuda Estado */}
            <tr className="border-t border-slate-100">
              <td className="px-3 py-2 font-medium sticky left-0 bg-white z-10 border-r border-slate-100">
                Ayuda Estado
                <div className="text-[9px] text-slate-400">subvención recibida</div>
              </td>
              {data.rows.map((r) => (
                <td key={r.month} className="px-1 py-1 text-right">
                  <EditableCell
                    value={r.ayuda_estado}
                    onCommit={(v) => handleEdit(r.month, "ayuda_estado", v)}
                  />
                </td>
              ))}
              <td className="px-3 py-2 text-right font-mono font-semibold bg-slate-50">
                {eur(data.totals.ayuda_estado)}
              </td>
            </tr>

            {/* Computed row: A pagar ET */}
            <tr className="border-t-2 border-slate-200 bg-slate-50/50">
              <td className="px-3 py-2 font-semibold sticky left-0 bg-slate-50/90 z-10 border-r border-slate-100">
                A pagar ET (50%)
                <div className="text-[9px] text-slate-400 font-normal">= (Gastos - Otros - Ayuda) / 2</div>
              </td>
              {data.rows.map((r) => (
                <td key={r.month} className="px-2 py-2 text-right font-mono">
                  {r.a_pagar_et > 0 ? eur(r.a_pagar_et) : <span className="text-slate-300">—</span>}
                </td>
              ))}
              <td className="px-3 py-2 text-right font-mono font-semibold bg-slate-100">
                {eur(data.totals.a_pagar_et)}
              </td>
            </tr>

            {/* Computed row: PAGADO ET */}
            <tr className="border-t border-slate-100">
              <td className="px-3 py-2 font-semibold sticky left-0 bg-white z-10 border-r border-slate-100">
                PAGADO ET
                <div className="text-[9px] text-slate-400 font-normal">desde facturas emitidas</div>
              </td>
              {data.rows.map((r) => (
                <td key={r.month} className="px-2 py-2 text-right font-mono">
                  {r.pagado_et > 0 ? eur(r.pagado_et) : <span className="text-slate-300">—</span>}
                </td>
              ))}
              <td className="px-3 py-2 text-right font-mono font-semibold bg-slate-50">
                {eur(data.totals.pagado_et)}
              </td>
            </tr>

            {/* Computed row: DEUDA ET (running balance) */}
            <tr className="border-t-2 border-slate-300 bg-slate-900 text-white">
              <td className="px-3 py-3 font-cabinet font-bold sticky left-0 bg-slate-900 z-10">
                DEUDA ET
                <div className="text-[9px] text-slate-400 font-normal">balance acumulado</div>
              </td>
              {data.rows.map((r) => (
                <td key={r.month}
                    className={`px-2 py-3 text-right font-mono font-semibold ${
                      r.deuda_et > 0 ? "text-rose-300" : r.deuda_et < 0 ? "text-emerald-300" : "text-slate-400"
                    }`}>
                  {eur(r.deuda_et)}
                </td>
              ))}
              <td className={`px-3 py-3 text-right font-mono font-bold bg-slate-800 ${
                finalDeuda > 0 ? "text-rose-300" : finalDeuda < 0 ? "text-emerald-300" : ""
              }`}>
                {eur(finalDeuda)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-slate-400">
        Tip: la cifra DEUDA ET en <span className="text-rose-600">rojo</span> indica
        que ET debe dinero a VdV; en <span className="text-emerald-600">verde</span>,
        lo contrario.
      </p>

      {/* Sheets preview (verify what the bot reads from the source) */}
      <div className="pt-6 border-t border-slate-100">
        <button
          onClick={togglePreview}
          className="flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900 font-medium"
          data-testid="toggle-sheet-preview"
        >
          <Sheet size={15} />
          <span>Ver Google Sheets de origen ({year})</span>
          <ChevronDown
            size={15}
            className={`transition-transform ${showPreview ? "rotate-180" : ""}`}
          />
          {previewLoading && <Loader2 size={13} className="animate-spin" />}
        </button>
        {showPreview && sheetPreview && (
          <div className="mt-4 space-y-2">
            <div className="text-xs text-slate-500">
              <span className="font-mono">{sheetPreview.spreadsheet_title}</span> →{" "}
              <span className="font-semibold">{sheetPreview.worksheet_title}</span>
              <span className="ml-2 text-slate-400">
                ({sheetPreview.rows.length} filas)
              </span>
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white max-h-[500px] overflow-y-auto">
              <table className="text-xs font-mono">
                <tbody>
                  {sheetPreview.rows.map((row, ri) => {
                    const first = (row[0] || "").trim().toLowerCase();
                    const isHighlighted = first.startsWith("et + vdv") || first.startsWith("et+vdv");
                    return (
                      <tr
                        key={ri}
                        className={`border-t border-slate-100 ${
                          isHighlighted ? "bg-amber-50 font-semibold" : ""
                        }`}
                      >
                        <td className="px-2 py-1 text-slate-400 text-[10px] w-8 text-right border-r border-slate-100 sticky left-0 bg-inherit">
                          {ri + 1}
                        </td>
                        {row.map((cell, ci) => (
                          <td
                            key={ci}
                            className="px-2 py-1 whitespace-nowrap"
                            title={cell}
                          >
                            {cell.length > 25 ? cell.slice(0, 25) + "…" : cell}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="text-[11px] text-slate-500">
              <span className="inline-block w-3 h-3 bg-amber-100 border border-amber-200 mr-1 align-middle"></span>
              La fila destacada es la que el sistema usa como base del cálculo (ET + VDV)
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
